import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { Tabs, Tab, MenuItem, InputAdornment } from '@material-ui/core';
import { FaUserTie } from 'react-icons/fa';
import * as Yup from 'yup';

import Paper from 'components/Paper';
import TextField from 'components/TextField';
import Button from 'components/Button';
import ClientesActions from 'store/ducks/clientes';
import NaturezasActions from 'store/ducks/naturezas';
import { clientesAPI } from 'services/apiRest';

import {
  Container,
  WrapperBody,
  WrapperContent,
  WrapperBottom,
  WrapperInput,
  InputRow,
  TabsContainer,
  TabPanel,
  AutocompleteWrapper,
  AutocompleteDropdown,
  AutocompleteItem,
} from './styles';

// Validação com Yup
const validationSchema = Yup.object().shape({
  nome: Yup.string().required('Nome é obrigatório'),
  cpfCnpj: Yup.string()
    .required('CPF/CNPJ é obrigatório')
    .test('cpf-cnpj', 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos', (value) => {
      if (!value) return false;
      const cleaned = value.replace(/\D/g, '');
      return cleaned.length === 11 || cleaned.length === 14;
    }),
  endereco: Yup.string(),
  cidade: Yup.string(),
  uf: Yup.string().max(2, 'UF deve ter 2 caracteres'),
  cep: Yup.string().test('cep', 'CEP deve ter 8 dígitos', (value) => {
    if (!value) return true;
    const cleaned = value.replace(/\D/g, '');
    return cleaned.length === 8 || cleaned.length === 0;
  }),
  email: Yup.string().email('Email inválido'),
  limiteCredito: Yup.number().min(0, 'Limite de crédito deve ser maior ou igual a zero'),
  limiteUtilizado: Yup.number().min(0, 'Limite utilizado deve ser maior ou igual a zero'),
});

function ClienteForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { clientes, isLoading } = useSelector((state) => state.clientes);
  const { naturezas: naturezasState, isLoading: isLoadingNaturezas } = useSelector((state) => state.naturezas);
  const isEdit = !!id;
  const isView = window.location.pathname.includes('/view/');
  
  // Verificar se há dados pré-preenchidos vindos de outra tela (ex: viagem)
  const dadosPreenchidos = location.state?.dadosPreenchidos || null;
  const returnTo = location.state?.returnTo || null;
  const [activeTab, setActiveTab] = useState(0);
  const [naturezaInput, setNaturezaInput] = useState('');
  const [showDropdownNatureza, setShowDropdownNatureza] = useState(false);
  const dropdownNaturezaRef = useRef(null);
  const clienteCarregadoRef = useRef(null);
  const [clienteCarregado, setClienteCarregado] = useState(null);

  // Converter naturezas de Immutable para array normal se necessário
  const naturezas = useMemo(() => {
    console.log('🔄 [ClienteForm] Convertendo naturezas:', {
      naturezasState,
      type: typeof naturezasState,
      isArray: Array.isArray(naturezasState),
      hasAsMutable: naturezasState && typeof naturezasState.asMutable === 'function',
      length: naturezasState?.length,
    });
    
    if (!naturezasState) {
      console.log('⚠️ [ClienteForm] naturezasState é null/undefined');
      return [];
    }
    
    let convertedArray = [];
    
    // Se já é um array normal, usa direto
    if (Array.isArray(naturezasState)) {
      convertedArray = naturezasState;
      console.log('✅ [ClienteForm] naturezasState já é array:', convertedArray.length);
    }
    // Se é um objeto Immutable (seamless-immutable), converte para array
    else if (naturezasState && typeof naturezasState === 'object') {
      // Tenta usar asMutable se disponível (seamless-immutable)
      if (typeof naturezasState.asMutable === 'function') {
        try {
          const mutable = naturezasState.asMutable({ deep: true });
          convertedArray = Array.isArray(mutable) ? mutable : [];
          console.log('✅ [ClienteForm] Convertido com asMutable:', convertedArray.length);
        } catch (e) {
          console.warn('❌ [ClienteForm] Erro ao converter Immutable com asMutable:', e);
        }
      }
      
      // Se ainda não foi convertido, tenta Array.from
      if (convertedArray.length === 0 && typeof naturezasState.length === 'number' && naturezasState.length >= 0) {
        try {
          convertedArray = Array.from(naturezasState);
          console.log('✅ [ClienteForm] Convertido com Array.from:', convertedArray.length);
        } catch (e) {
          console.warn('❌ [ClienteForm] Erro ao converter com Array.from:', e);
        }
      }
      
      // Última tentativa: usar spread operator
      if (convertedArray.length === 0) {
        try {
          convertedArray = [...naturezasState];
          console.log('✅ [ClienteForm] Convertido com spread:', convertedArray.length);
        } catch (e) {
          console.warn('❌ [ClienteForm] Erro ao converter com spread:', e);
        }
      }
    }
    
    // Filtrar e validar naturezas - garantir que todas tenham id, codigo e descricao
    const validNaturezas = convertedArray.filter(n => {
      if (!n || typeof n !== 'object') return false;
      // Verifica se tem pelo menos id ou codigo
      return (n.id !== undefined && n.id !== null) || (n.codigo !== undefined && n.codigo !== null);
    });
    
    console.log('📊 [ClienteForm] Naturezas convertidas:', {
      convertedArrayLength: convertedArray.length,
      validNaturezasLength: validNaturezas.length,
      primeiraNatureza: validNaturezas[0],
    });
    
    return validNaturezas;
  }, [naturezasState]);

  // Buscar cliente individualmente se estiver editando ou visualizando
  useEffect(() => {
    if ((isEdit || isView) && id && !clienteCarregado) {
      const buscarClienteIndividual = async () => {
        try {
          const cliente = await clientesAPI.buscarPorId(id);
          console.log('📥 [ClienteForm] Cliente buscado individualmente:', {
            cliente,
            credito: cliente.credito,
            naturezaId: cliente.credito?.naturezaId,
            naturezaIdType: typeof cliente.credito?.naturezaId,
          });
          setClienteCarregado(cliente);
        } catch (error) {
          console.error('❌ [ClienteForm] Erro ao buscar cliente:', error);
          // Fallback: buscar da lista se falhar
          if (clientes.length === 0) {
            dispatch(ClientesActions.getClientesRequest());
          }
        }
      };
      
      buscarClienteIndividual();
    }
  }, [dispatch, isEdit, isView, id, clienteCarregado, clientes.length]);

  // Buscar naturezas sempre que o componente montar
  useEffect(() => {
    dispatch(NaturezasActions.getNaturezasRequest());
  }, [dispatch]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownNaturezaRef.current && !dropdownNaturezaRef.current.contains(event.target)) {
        setShowDropdownNatureza(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Usar cliente carregado individualmente se disponível, senão buscar da lista
  const cliente = (isEdit || isView) 
    ? (clienteCarregado || clientes.find((item) => String(item.id) === String(id)))
    : null;
  
  // Debug: verificar estrutura do cliente
  useEffect(() => {
    if (cliente) {
      console.log('🔍 [ClienteForm] Estrutura do cliente encontrado:', {
        id: cliente.id,
        nome: cliente.nome,
        credito: cliente.credito,
        creditoType: typeof cliente.credito,
        naturezaId: cliente.credito?.naturezaId,
        naturezaIdType: typeof cliente.credito?.naturezaId,
        creditoKeys: cliente.credito ? Object.keys(cliente.credito) : [],
      });
    } else if (isEdit || isView) {
      console.warn('⚠️ [ClienteForm] Cliente não encontrado na lista:', {
        id,
        clientesLength: clientes.length,
        clientesIds: clientes.map(c => c.id),
      });
    }
  }, [cliente, id, isEdit, isView, clientes]);

  // Detectar tipo de pessoa baseado no CPF/CNPJ
  const detectTipoPessoa = (cpfCnpj) => {
    if (!cpfCnpj) return 'PF';
    const cleaned = cpfCnpj.replace(/\D/g, '');
    return cleaned.length === 11 ? 'PF' : 'PJ';
  };

  const formik = useFormik({
    initialValues: {
      nome: cliente?.nome || dadosPreenchidos?.nome || '',
      nomeFantasia: cliente?.nomeFantasia || dadosPreenchidos?.nomeFantasia || '',
      cpfCnpj: cliente?.cpfCnpj || dadosPreenchidos?.cpfCnpj || '',
      tipoPessoa: cliente?.tipoPessoa || dadosPreenchidos?.tipoPessoa || detectTipoPessoa(dadosPreenchidos?.cpfCnpj) || 'PF',
      endereco: cliente?.endereco || dadosPreenchidos?.endereco || '',
      numero: cliente?.numero || dadosPreenchidos?.numero || '',
      complemento: cliente?.complemento || dadosPreenchidos?.complemento || '',
      bairro: cliente?.bairro || dadosPreenchidos?.bairro || '',
      cidade: cliente?.cidade || dadosPreenchidos?.cidade || '',
      uf: cliente?.uf || dadosPreenchidos?.uf || '',
      cep: cliente?.cep || dadosPreenchidos?.cep || '',
      numeroContato: cliente?.numeroContato || dadosPreenchidos?.numeroContato || '',
      nomeContato: cliente?.nomeContato || dadosPreenchidos?.nomeContato || '',
      email: cliente?.email || dadosPreenchidos?.email || '',
      prazoPagamento: cliente?.prazoPagamento || dadosPreenchidos?.prazoPagamento || '',
      formaPagamento: cliente?.formaPagamento || dadosPreenchidos?.formaPagamento || '',
      taxaEmissao: cliente?.taxaEmissao || dadosPreenchidos?.taxaEmissao || '',
      dataInclusao: cliente?.dataInclusao || dadosPreenchidos?.dataInclusao || new Date().toISOString().split('T')[0],
      // Crédito
      limiteCredito: cliente?.credito?.limiteCredito || dadosPreenchidos?.limiteCredito || 0,
      limiteUtilizado: cliente?.credito?.limiteUtilizado || dadosPreenchidos?.limiteUtilizado || 0,
      natureza: cliente?.credito?.naturezaId || dadosPreenchidos?.natureza || '',
    },
    validationSchema,
    enableReinitialize: false, // Desabilitar para evitar que reinicialize e perca valores
    onSubmit: (values) => {
      // Converter naturezaId - é UUID (string), não número
      let naturezaId = null;
      if (values.natureza !== undefined && values.natureza !== null && values.natureza !== '') {
        if (typeof values.natureza === 'string') {
          // Se for string vazia, null
          const trimmed = values.natureza.trim();
          if (trimmed === '') {
            naturezaId = null;
          } else {
            // UUID é string
            naturezaId = trimmed;
          }
        } else {
          // Se for número, converter para string (UUID)
          naturezaId = String(values.natureza);
        }
      }

      const naturezaSelecionada = naturezaId ? naturezas.find(n => {
        // Comparar como string para funcionar com UUID
        return String(n.id) === String(naturezaId);
      }) : null;

      const clienteData = {
        ...values,
        id: cliente?.id,
        tipoPessoa: detectTipoPessoa(values.cpfCnpj),
        credito: {
          limiteCredito: parseFloat(values.limiteCredito) || 0,
          limiteUtilizado: parseFloat(values.limiteUtilizado) || 0,
          natureza: naturezaSelecionada?.descricao || null,
          naturezaId: naturezaId || null, // Garantir que seja null se vazio
        },
      };

      console.log('📤 [ClienteForm] Dados sendo enviados:', {
        naturezaId,
        naturezaSelecionada,
        natureza: clienteData.credito.natureza,
        credito: clienteData.credito,
        empresa_id: clienteData.empresa_id,
        clienteData: clienteData,
      });

      const handleSuccess = () => {
        // Se houver returnTo (vindo de outra tela como viagem), voltar para lá
        if (returnTo) {
          history.push(returnTo);
        } else {
          history.push('/financeiro/clientes');
        }
      };

      if (isEdit) {
        dispatch(
          ClientesActions.updateClienteRequest(clienteData, handleSuccess)
        );
      } else {
        dispatch(
          ClientesActions.addClienteRequest(clienteData, handleSuccess)
        );
      }
    },
  });

  useEffect(() => {
    if (cliente && (isEdit || isView) && cliente.id !== clienteCarregadoRef.current) {
      clienteCarregadoRef.current = cliente.id;
      
      console.log('📥 [ClienteForm] Cliente completo recebido:', {
        cliente,
        credito: cliente.credito,
        naturezaId: cliente.credito?.naturezaId,
        naturezaIdType: typeof cliente.credito?.naturezaId,
        taxaEmissao: cliente.taxaEmissao,
      });
      
      const naturezaId = cliente.credito?.naturezaId || '';
      
      // Atualizar valores diretamente sem setTimeout para evitar race conditions
      formik.setValues({
        nome: cliente.nome || '',
        nomeFantasia: cliente.nomeFantasia || '',
        cpfCnpj: cliente.cpfCnpj || '',
        tipoPessoa: cliente.tipoPessoa || 'PF',
        endereco: cliente.endereco || '',
        numero: cliente.numero || '',
        complemento: cliente.complemento || '',
        bairro: cliente.bairro || '',
        cidade: cliente.cidade || '',
        uf: cliente.uf || '',
        cep: cliente.cep || '',
        numeroContato: cliente.numeroContato || '',
        nomeContato: cliente.nomeContato || '',
        email: cliente.email || '',
        prazoPagamento: cliente.prazoPagamento || '',
        formaPagamento: cliente.formaPagamento || '',
        taxaEmissao: cliente.taxaEmissao || '',
        dataInclusao: cliente.dataInclusao ? cliente.dataInclusao.split('T')[0] : new Date().toISOString().split('T')[0],
        limiteCredito: cliente.credito?.limiteCredito || 0,
        limiteUtilizado: cliente.credito?.limiteUtilizado || 0,
        natureza: naturezaId,
      }, false);
      
      console.log('📥 [ClienteForm] Formik atualizado com todos os valores, incluindo taxaEmissao:', cliente.taxaEmissao);
      
      // Tentar preencher o input imediatamente se as naturezas já estiverem carregadas
      if (naturezaId && naturezas.length > 0) {
        const naturezaSelecionada = naturezas.find(n => String(n.id) === String(naturezaId));
        if (naturezaSelecionada) {
          const inputValue = `${naturezaSelecionada.codigo || ''} - ${naturezaSelecionada.descricao || ''}`;
          console.log('✅ [ClienteForm] Preenchendo input imediatamente:', inputValue);
          setNaturezaInput(inputValue);
        } else {
          console.warn('⚠️ [ClienteForm] Natureza não encontrada nas naturezas carregadas:', {
            naturezaId,
            naturezasDisponiveis: naturezas.map(n => ({ id: String(n.id), codigo: n.codigo })),
          });
        }
      } else if (naturezaId && naturezas.length === 0) {
        console.log('⏳ [ClienteForm] Aguardando carregamento das naturezas para preencher input...');
      }
    }
  }, [cliente?.id, isEdit, isView, formik]);

  // Sincronizar input de natureza quando natureza for selecionada ou quando naturezas forem carregadas
  useEffect(() => {
    const naturezaId = formik.values.natureza;
    
    // Se há uma natureza selecionada no formik e naturezas foram carregadas
    if (naturezaId && naturezas.length > 0) {
      const naturezaSelecionada = naturezas.find(n => {
        return String(n.id) === String(naturezaId);
      });
      
      if (naturezaSelecionada) {
        const novoInput = `${naturezaSelecionada.codigo || ''} - ${naturezaSelecionada.descricao || ''}`;
        
        // Sempre atualizar se o valor for diferente ou se o input estiver vazio
        if (!naturezaInput || naturezaInput !== novoInput) {
          console.log('🔄 [ClienteForm] Atualizando input de natureza:', {
            naturezaId,
            novoInput,
            naturezaInputAtual: naturezaInput,
          });
          setNaturezaInput(novoInput);
        }
      } else {
        console.warn('⚠️ [ClienteForm] Natureza não encontrada:', {
          naturezaId,
          naturezasDisponiveis: naturezas.map(n => ({ id: String(n.id), codigo: n.codigo })),
        });
      }
    } else if (!naturezaId && naturezaInput) {
      // Se não há natureza selecionada mas o input tem valor, limpar
      console.log('🧹 [ClienteForm] Limpando input de natureza');
      setNaturezaInput('');
    }
  }, [formik.values.natureza, naturezas, naturezaInput]);

  // Preservar taxaEmissao se ela for perdida
  useEffect(() => {
    if (cliente && (isEdit || isView) && cliente.taxaEmissao && !formik.values.taxaEmissao) {
      console.log('🔄 [ClienteForm] Restaurando taxaEmissao que foi perdida:', cliente.taxaEmissao);
      formik.setFieldValue('taxaEmissao', cliente.taxaEmissao, false);
    }
  }, [cliente?.taxaEmissao, formik.values.taxaEmissao, isEdit, isView, cliente]);

  // Debug: verificar se naturezas foram carregadas
  useEffect(() => {
    console.log('🔍 [ClienteForm] Naturezas no estado:', {
      count: naturezas?.length || 0,
      isLoading: isLoadingNaturezas,
      isArray: Array.isArray(naturezas),
      naturezaSelecionada: formik.values.natureza,
      naturezaInput: naturezaInput,
    });
    
    // Log detalhado das primeiras naturezas
    if (naturezas && naturezas.length > 0) {
      console.log('📋 [ClienteForm] Primeiras 3 naturezas:', naturezas.slice(0, 3));
    }
  }, [naturezas, isLoadingNaturezas, formik.values.natureza, naturezaInput]);

  // Formatar CPF/CNPJ
  const formatCpfCnpj = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 11) {
      // CPF: 000.000.000-00
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      // CNPJ: 00.000.000/0000-00
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  // Formatar CEP
  const formatCep = (value) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const handleCpfCnpjChange = (e) => {
    const formatted = formatCpfCnpj(e.target.value);
    formik.setFieldValue('cpfCnpj', formatted);
    formik.setFieldValue('tipoPessoa', detectTipoPessoa(formatted));
  };

  const handleCepChange = (e) => {
    const formatted = formatCep(e.target.value);
    formik.setFieldValue('cep', formatted);
  };

  // Filtrar naturezas baseado no input
  const naturezasFiltradas = useMemo(() => {
    if (!naturezaInput || naturezaInput.trim() === '') {
      return naturezas;
    }
    const searchTerm = naturezaInput.toLowerCase();
    return naturezas.filter((n) => {
      const codigo = String(n.codigo || '').toLowerCase();
      const descricao = String(n.descricao || '').toLowerCase();
      return codigo.includes(searchTerm) || descricao.includes(searchTerm);
    });
  }, [naturezaInput, naturezas]);

  // Selecionar natureza
  const handleSelecionarNatureza = (natureza) => {
    formik.setFieldValue('natureza', natureza.id);
    setNaturezaInput(`${natureza.codigo || ''} - ${natureza.descricao || ''}`);
    setShowDropdownNatureza(false);
  };

  return (
    <Container>
      <Paper
        title={isView ? 'VISUALIZAR CLIENTE' : isEdit ? 'EDITAR CLIENTE' : 'NOVO CLIENTE'}
        icon={<FaUserTie size={24} style={{ color: '#122d58', marginRight: '25px' }} />}
        rightContent={
          !isView && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Button
                label="Cancelar"
                onClick={() => history.push('/financeiro/clientes')}
                type="button"
                width="120px"
              />
              <Button
                label={isEdit ? 'Atualizar' : 'Salvar'}
                type="button"
                width="120px"
                onClick={() => {
                  formik.handleSubmit();
                }}
                disabled={isLoading}
              />
            </div>
          )
        }
      >
        <form onSubmit={formik.handleSubmit}>
          <TabsContainer>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Dados do Cliente" />
              <Tab label="Crédito do Cliente" />
            </Tabs>
          </TabsContainer>

          {activeTab === 0 && (
          <TabPanel>
            <WrapperBody>
              <WrapperContent>
                <InputRow>
                  <WrapperInput>
                    <TextField
                      label="Nome *"
                      name="nome"
                      value={formik.values.nome}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.nome && Boolean(formik.errors.nome)}
                      helperText={formik.touched.nome && formik.errors.nome}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                  <WrapperInput>
                    <TextField
                      label="Nome Fantasia"
                      name="nomeFantasia"
                      value={formik.values.nomeFantasia}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                </InputRow>

                <InputRow>
                  <WrapperInput>
                    <TextField
                      label="CPF/CNPJ *"
                      name="cpfCnpj"
                      value={formik.values.cpfCnpj}
                      onChange={handleCpfCnpjChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.cpfCnpj && Boolean(formik.errors.cpfCnpj)}
                      helperText={formik.touched.cpfCnpj && formik.errors.cpfCnpj}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                  <WrapperInput>
                    <TextField
                      label="Tipo de Pessoa"
                      name="tipoPessoa"
                      value={formik.values.tipoPessoa === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                      fullWidth
                      color="secondary"
                      disabled
                    />
                  </WrapperInput>
                </InputRow>

                <InputRow>
                  <WrapperInput style={{ flex: 2 }}>
                    <TextField
                      label="Endereço"
                      name="endereco"
                      value={formik.values.endereco}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                  <WrapperInput style={{ flex: 1 }}>
                    <TextField
                      label="Número"
                      name="numero"
                      value={formik.values.numero}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                  <WrapperInput style={{ flex: 1 }}>
                    <TextField
                      label="Complemento"
                      name="complemento"
                      value={formik.values.complemento}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                </InputRow>

                <InputRow>
                  <WrapperInput>
                    <TextField
                      label="Bairro"
                      name="bairro"
                      value={formik.values.bairro}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                  <WrapperInput>
                    <TextField
                      label="Cidade"
                      name="cidade"
                      value={formik.values.cidade}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                  <WrapperInput style={{ width: '100px' }}>
                    <TextField
                      label="UF"
                      name="uf"
                      value={formik.values.uf}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase().substring(0, 2);
                        formik.setFieldValue('uf', value);
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.uf && Boolean(formik.errors.uf)}
                      helperText={formik.touched.uf && formik.errors.uf}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                      inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                    />
                  </WrapperInput>
                  <WrapperInput>
                    <TextField
                      label="CEP"
                      name="cep"
                      value={formik.values.cep}
                      onChange={handleCepChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.cep && Boolean(formik.errors.cep)}
                      helperText={formik.touched.cep && formik.errors.cep}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                      inputProps={{ maxLength: 9 }}
                    />
                  </WrapperInput>
                </InputRow>

                <InputRow>
                  <WrapperInput>
                    <TextField
                      label="Número de Contato"
                      name="numeroContato"
                      value={formik.values.numeroContato}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                  <WrapperInput>
                    <TextField
                      label="Nome do Contato"
                      name="nomeContato"
                      value={formik.values.nomeContato}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                </InputRow>

                <InputRow>
                  <WrapperInput>
                    <TextField
                      label="Email"
                      name="email"
                      type="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                  <WrapperInput>
                    <TextField
                      label="Data de Inclusão"
                      name="dataInclusao"
                      type="date"
                      value={formik.values.dataInclusao}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </WrapperInput>
                </InputRow>

                <InputRow>
                  <WrapperInput>
                    <TextField
                      label="Prazo de Pagamento (dias)"
                      name="prazoPagamento"
                      type="number"
                      value={formik.values.prazoPagamento}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                      inputProps={{ min: 0, step: 1 }}
                      placeholder="Ex: 10, 20, 30"
                    />
                  </WrapperInput>
                  <WrapperInput>
                    <TextField
                      label="Forma de Pagamento"
                      name="formaPagamento"
                      select
                      value={formik.values.formaPagamento || ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    >
                      <MenuItem value="">Selecione...</MenuItem>
                      <MenuItem value="Cartao de Credito">Cartão de Crédito</MenuItem>
                      <MenuItem value="Boleto">Boleto</MenuItem>
                      <MenuItem value="Pix">Pix</MenuItem>
                    </TextField>
                  </WrapperInput>
                </InputRow>

                <InputRow>
                  <WrapperInput>
                    <TextField
                      label="Taxa de Emissão (%)"
                      name="taxaEmissao"
                      type="number"
                      value={formik.values.taxaEmissao}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                      inputProps={{ min: 0, step: 0.01, max: 100 }}
                      placeholder="Ex: 5.50"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  </WrapperInput>
                </InputRow>
              </WrapperContent>
            </WrapperBody>
          </TabPanel>
          )}

          {activeTab === 1 && (
          <TabPanel>
            <WrapperBody>
              <WrapperContent>
                <InputRow>
                  <WrapperInput>
                    <TextField
                      label="Limite de Crédito"
                      name="limiteCredito"
                      type="number"
                      inputProps={{ step: '0.01', min: '0' }}
                      value={formik.values.limiteCredito}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.limiteCredito && Boolean(formik.errors.limiteCredito)}
                      helperText={formik.touched.limiteCredito && formik.errors.limiteCredito}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                  <WrapperInput>
                    <TextField
                      label="Limite Utilizado"
                      name="limiteUtilizado"
                      type="number"
                      inputProps={{ step: '0.01', min: '0' }}
                      value={formik.values.limiteUtilizado}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.limiteUtilizado && Boolean(formik.errors.limiteUtilizado)}
                      helperText={formik.touched.limiteUtilizado && formik.errors.limiteUtilizado}
                      fullWidth
                      color="secondary"
                      disabled={isView}
                    />
                  </WrapperInput>
                  <WrapperInput>
                    <TextField
                      label="Limite Disponível"
                      name="limiteDisponivel"
                      value={Number(formik.values.limiteCredito || 0) - Number(formik.values.limiteUtilizado || 0)}
                      fullWidth
                      color="secondary"
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </WrapperInput>
                </InputRow>

                <InputRow>
                  <WrapperInput>
                    <AutocompleteWrapper ref={dropdownNaturezaRef}>
                      <TextField
                        label="Natureza"
                        name="natureza"
                        placeholder="Digite para buscar a natureza"
                        value={naturezaInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNaturezaInput(value);
                          setShowDropdownNatureza(true);
                          // Se limpar o campo, limpar também o valor do formik
                          if (!value) {
                            formik.setFieldValue('natureza', '');
                          }
                        }}
                        onFocus={() => {
                          if (naturezas.length > 0) {
                            setShowDropdownNatureza(true);
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            setShowDropdownNatureza(false);
                            formik.handleBlur({ target: { name: 'natureza' } });
                          }, 200);
                        }}
                        error={formik.touched.natureza && Boolean(formik.errors.natureza)}
                        helperText={
                          formik.touched.natureza && formik.errors.natureza
                            ? formik.errors.natureza
                            : isLoadingNaturezas
                            ? 'Carregando naturezas...'
                            : naturezas.length > 0
                            ? `${naturezas.length} natureza(s) disponível(is)`
                            : 'Nenhuma natureza cadastrada'
                        }
                        disabled={isView || isLoadingNaturezas}
                        fullWidth
                        color="secondary"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      {showDropdownNatureza && naturezasFiltradas.length > 0 && (
                        <AutocompleteDropdown>
                          {naturezasFiltradas.slice(0, 20).map((natureza) => (
                            <AutocompleteItem
                              key={natureza.id}
                              onClick={() => handleSelecionarNatureza(natureza)}
                            >
                              <div>
                                <strong>{natureza.codigo || ''}</strong> - {natureza.descricao || ''}
                                <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
                                  ({natureza.tipo === 'Sintetica' ? 'Sintética' : natureza.tipo === 'Analitica' ? 'Analítica' : natureza.tipo || ''} - {natureza.classificacao || ''})
                                </span>
                              </div>
                            </AutocompleteItem>
                          ))}
                          {naturezasFiltradas.length > 20 && (
                            <AutocompleteItem style={{ textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                              ... e mais {naturezasFiltradas.length - 20} natureza(s)
                            </AutocompleteItem>
                          )}
                        </AutocompleteDropdown>
                      )}
                    </AutocompleteWrapper>
                  </WrapperInput>
                </InputRow>
              </WrapperContent>
            </WrapperBody>
            
          </TabPanel>
          )}
        </form>
      </Paper>

      
    </Container>
  );
}

export default ClienteForm;

